/**
 * Copyright © Smartex Canada All rights reserved.
 * See LICENSE for license details.
 */

import type { SpecificEventListener, Timeout } from "@/types";
import { transitionEvent } from "@/utils/dom/style";
import { getTemplate, getTemplateChildren } from "@/utils/dom/component";
import { getElements } from "@/utils/dom/element";
import { FocusTrap } from "@/utils/focus-trap";
import { uniqueId } from "@/utils/strings";
import { Component } from "./component";

let modalTemplate: HTMLTemplateElement;

export class Modal extends Component<ModalEvents> {
    static templateSelector: 'modal-template';

    static ANIMATION_OPEN_DURATION = 120;
    static ANIMATION_CLOSE_DURATION = 75;

    static defaults: Partial<ModalOptions> = {
        autoOpen: false,
        appendTo: 'body',
        escapeKeyCloses: true,
        clickableOverlay: true,
        bodyScrollLockClass: 'modal-scroll-lock',
        initialFocusSelector: '[data-modal-initial-focus]',
        modalClasses: [],
        buttons: []
    };

    readonly classes = {
        modal: 'modal',
        wrapper: 'modal__inner',
        header: 'modal__header',
        content: 'modal__content',
        footer: 'modal__footer',
        title: 'modal__title',
        container: 'modal__container',
        overlay: 'modal__overlay',
        placeholder: 'modal__placeholder',
        closeButton: 'action-close',
        hidden: 'hidden',
        open: 'modal--open',
        opening: 'modal--opening',
        closing: 'modal--closing'
    };

    public element: HTMLElement;
    public config: ModalOptions;
    public id: string;
    public isOpen: boolean = false;

    protected parent: HTMLElement;
    protected container: HTMLElement;
    protected placeholder: HTMLElement;
    protected modal: HTMLElement;
    protected titleElement!: HTMLElement;

    protected keydownListener: SpecificEventListener<'keydown'>;

    protected animationTimer: Timeout | number = 0;
    protected animationFrame: number = 0;

    protected focusTrap: FocusTrap;

    constructor(element: HTMLElement, options: Partial<ModalOptions> = {}) {
        super();

        this.element = element;
        this.config = { ...Modal.defaults, ...options } as ModalOptions;
        this.id = uniqueId();

        if (!modalTemplate) {
            modalTemplate = this._createTemplate();
        }

        this.keydownListener = (event) => this._handleKeydown(event);

        this.parent = this._getParentElement();
        this.placeholder = this._createPlaceholder();
        this.container = this._createContainer();
        this.modal = this._createModal(this.config.template || modalTemplate);

        this.focusTrap = new FocusTrap(this.modal, {
            initialFocusElement: this._getInitialFocusElement()
        });

        if (this.config.autoOpen) {
            this.openModal();
        }
    }

    /**
     * @inheritdoc
     */
    public override destroy(): void {
        super.destroy();

        if (this.isOpen) {
            this.closeModal().then(() => this._destroy());
            return;
        }

        this._destroy();
    }

    private _destroy() {
        this.placeholder.before(this.element);
        this.placeholder.remove();

        this.modal.remove();

        if (this.container.childElementCount === 0) {
            this.container.remove();
        }

        this.parent = null as any;
        this.placeholder = null as any;
        this.modal = null as any;
        this.container = null as any;
        this.element = null as any;
    }

    /**
     * Open modal dialog
     */
    public openModal(): Promise<void> {
        if (this.isOpen) {
            return Promise.resolve();
        }

        this.isOpen = true;
        this.trigger('opening');

        this.parent.classList.add(this.config.bodyScrollLockClass);
        this.modal.classList.remove(this.classes.closing);
        this.modal.classList.add(this.classes.opening);
        this.modal.addEventListener('keydown', this.keydownListener);
        this.modal.setAttribute('aria-modal', 'true');

        return new Promise(resolve => {
            const animationEnd = () => {
                if (transitionEvent) {
                    this.modal.removeEventListener(transitionEvent, animationEnd);
                }
                this._handleAnimationTimerEnd();
                this.focusTrap.trapFocus();
                this._onOpen();
                this.trigger('opened');
                resolve();
            };

            this._runNextAnimationFrame(() => {
                this.modal.classList.add(this.classes.open);

                if (transitionEvent) {
                    this.modal.addEventListener(transitionEvent, animationEnd);
                } else {
                    this.animationTimer = setTimeout(animationEnd, Modal.ANIMATION_OPEN_DURATION);
                }
            });
        });
    }

    /**
     * Close modal dialog
     */
    public closeModal(): Promise<void> {
        if (!this.isOpen) {
            return Promise.resolve();
        }

        this.modal.setAttribute('aria-modal', 'false');
        this.modal.removeEventListener('keydown', this.keydownListener);
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = 0;
        this.isOpen = false;
        this.trigger('closing');

        this.modal.classList.add(this.classes.closing);
        this.modal.classList.remove(this.classes.opening);

        return new Promise(resolve => {
            const animationEnd = () => {
                if (transitionEvent) {
                    this.modal.removeEventListener(transitionEvent, animationEnd);
                }
                this.focusTrap.releaseFocus();
                this._handleAnimationTimerEnd();
                this._onClose();
                this.trigger('closed');
                resolve();
            };

            this._runNextAnimationFrame(() => {
                this.modal.classList.remove(this.classes.open);
                if (!this._getActiveCount()) {
                    this.parent.classList.remove(this.config.bodyScrollLockClass);
                }

                if (transitionEvent) {
                    this.modal.addEventListener(transitionEvent, animationEnd);
                } else {
                    this.animationTimer = setTimeout(animationEnd, Modal.ANIMATION_CLOSE_DURATION);
                }
            });
        });
    }

    /**
     * Set modal title
     */
    public setTitle(title: string) {
        this.config.title = title;

        this.titleElement.textContent = title;
        this.titleElement.classList.toggle(this.classes.hidden, !title);
    }

    protected _onOpen() {}

    protected _onClose() {}

    protected _handleKeydown(event: KeyboardEvent) {
        if (event.key.toLowerCase() === 'escape') {
            this.config.escapeKeyCloses && this.closeModal();
        }
    }

    protected _runNextAnimationFrame(callback: () => void) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = requestAnimationFrame(() => {
            this.animationFrame = 0;
            clearTimeout(this.animationTimer);
            this.animationTimer = setTimeout(callback);
        });
    }

    protected _handleAnimationTimerEnd() {
        this.animationTimer = 0;
        this.modal.classList.remove(this.classes.opening, this.classes.closing);
    }

    /**
     * Retrieve active modals in same container
     */
    protected _getActiveCount() {
        return getElements(
            '.' + this.classes.modal + '.' + this.classes.open,
            this.container
        ).length;
    }

    /**
     * Retrieve element to append modal
     */
    protected _getParentElement(): HTMLElement {
        const { appendTo } = this.config;

        return typeof appendTo === 'string'
            ? (document.querySelector<HTMLElement>(appendTo) || document.body)
            : appendTo;
    }

    /**
     * Create global modal container element
     */
    protected _createContainer(): HTMLElement {
        let container = getElements('.' + this.classes.container, this.parent)
            .find(container => container.parentElement === this.parent);

        if (!container) {
            container = document.createElement('div');
            container.classList.add(this.classes.container);
            this.parent.append(container);
        }

        return container;
    }

    /**
     * Create placeholder for element
     */
    private _createPlaceholder(): HTMLElement {
        const placeholder = document.createElement('span');

        placeholder.classList.add(this.classes.placeholder);
        this.element.after(placeholder);

        return placeholder;
    }

    /**
     * Create modal element
     */
    protected _createModal(template: HTMLTemplateElement): HTMLElement {
        const modal = getTemplateChildren(template)[0];
        const header = modal.querySelector<HTMLElement>('.' + this.classes.header)!;
        const content = modal.querySelector<HTMLElement>('.' + this.classes.content)!;
        const footer = modal.querySelector<HTMLElement>('.' + this.classes.footer)!;

        let overlay = modal.querySelector<HTMLElement>('.' + this.classes.overlay);
        if (!overlay) {
            overlay = document.createElement('div');
            overlay.classList.add(this.classes.overlay);
            modal.prepend(overlay);
        }
        if (this.config.clickableOverlay) {
            overlay.addEventListener('click', () => this.closeModal());
        }

        let title = header.querySelector<HTMLElement>('.' + this.classes.title);
        if (!title) {
            title = document.createElement('h2');
            title.classList.add(this.classes.title);
            header.prepend(title);
        }
        title.id = 'modal-title-' + this.id;
        this.titleElement = title;

        if (this.config.title) {
            title.textContent = this.config.title;
        } else {
            title.classList.add(this.classes.hidden);
        }

        let closeButton = header.querySelector<HTMLElement>('.' + this.classes.closeButton);
        if (!closeButton) {
            closeButton = document.createElement('button');
            closeButton.classList.add('button', this.classes.closeButton);
            header.append(closeButton);
        }
        closeButton.addEventListener('click', () => this.closeModal());

        content.id = 'modal-content-' + this.id;
        content.append(this.element);

        if (this.config.buttons && this.config.buttons.length) {
            footer.append(...this._createButtons());
        } else {
            footer.remove();
        }

        modal.role = 'dialog';
        modal.setAttribute('aria-labelledby', title.id);
        modal.setAttribute('aria-describedby', content.id);
        if (this.config.modalClasses?.length) {
            modal.classList.add(...this.config.modalClasses.filter(name => name.trim()));
        }
        this.container.append(modal);

        return modal;
    }

    /**
     * Create modal buttons
     */
    protected _createButtons() {
        return this.config.buttons.map(data => {
            const { classes, action } = data;
            const button = document.createElement('button');
            const text = document.createElement('span');

            text.textContent = data.text;
            button.append(text);

            if (classes) {
                button.classList.add(...(Array.isArray(classes) ? classes : [classes]));
            }

            if (action) {
                button.addEventListener('click', () => this._buttonHandler(action));
            }

            return button;
        });
    }

    /**
     * Handle button action
     */
    protected _buttonHandler(action: ModalButtonAction) {
        if (typeof action === 'function') {
            action.call(this as any);
        }
        else if (typeof action === 'string') {
            if (typeof (this as any)[action] === 'function') {
                (this as any)[action].call(this);
            }
        }
    }

    protected _getInitialFocusElement(): HTMLElement | null {
        return this.config.initialFocusSelector
            && this.modal.querySelector<HTMLElement>(this.config.initialFocusSelector)
            || null;
    }

    /**
     * Create global modal template
     */
    private _createTemplate(): HTMLTemplateElement {
        let template = getTemplate(Modal.templateSelector);

        if (!template) {
            template = document.createElement('template');
            const modal = document.createElement('aside');
            const overlay = document.createElement('div');
            const wrapper = document.createElement('div');

            const header = document.createElement('header');
            const content = document.createElement('section');
            const footer = document.createElement('footer');

            const title = document.createElement('h2');
            const closeButton = document.createElement('button');

            modal.tabIndex = 0;
            modal.role = 'dialog';
            modal.dataset.role = "modal";
            modal.classList.add(this.classes.modal);

            overlay.classList.add(this.classes.overlay);
            wrapper.classList.add(this.classes.wrapper);

            header.classList.add(this.classes.header);
            content.classList.add(this.classes.content);
            footer.classList.add(this.classes.footer);

            title.classList.add(this.classes.title);
            closeButton.type = 'button';
            closeButton.classList.add('button', this.classes.closeButton);

            header.append(title, closeButton);
            wrapper.append(header, content, footer);
            modal.append(overlay, wrapper);
            template.content.append(modal);
        }

        return template;
    }
}

export interface ModalEvents {
    opening: never
    opened: never
    closing: never
    closed: never
}

export interface ModalOptions {
    autoOpen: boolean
    appendTo: HTMLElement | string
    template?: HTMLTemplateElement
    title?: string
    buttons: ModalButton[]
    escapeKeyCloses: boolean
    clickableOverlay: boolean
    bodyScrollLockClass: string
    initialFocusSelector: string
    modalClasses?: string[]
}

export interface ModalButton {
    text: string
    classes?: string | string[]
    action?: ModalButtonAction
}

export type ModalButtonAction = string | ((this: Modal) => void);
